import React from "react";
import { Box, Typography, Alert, Tabs, Tab, Paper, Table, TableHead, TableRow, TableCell, TableBody, CircularProgress, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, IconButton } from "@mui/material";
import { useEffect, useState } from "react";
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';
import { backendUrl, API_KEYS } from '../config';

const API_URL = backendUrl;

export default function GroupsPage() {
  const { t } = useTranslation();
  // Onglet actif : 0 = local, 1 = app
  const [tab, setTab] = useState(0);
  // Deux listes séparées
  const [localGroups, setLocalGroups] = useState([]);
  const [appGroups, setAppGroups] = useState([]);
  const [loadingLocal, setLoadingLocal] = useState(true);
  const [loadingApp, setLoadingApp] = useState(true);
  const [errorLocal, setErrorLocal] = useState(null);
  const [errorApp, setErrorApp] = useState(null);

  // --- FETCHS SÉPARÉS ---
  const fetchLocalGroups = async () => {
    setLoadingLocal(true); setErrorLocal(null);
    try {
      const res = await fetch(`${API_URL}/local-groups/`, {
        headers: {
          'x-api-key': API_KEYS.key,
          'x-api-secret': API_KEYS.secret,
          'accept': 'application/json',
        },
      });
      if (!res.ok) throw new Error("Erreur lors du chargement des groupes locaux");
      setLocalGroups(await res.json());
    } catch (e) { setErrorLocal(e.toString()); }
    finally { setLoadingLocal(false); }
  };
  const fetchAppGroups = async () => {
    setLoadingApp(true); setErrorApp(null);
    try {
      const res = await fetch(`${API_URL}/app-groups/`, {
        headers: {
          'x-api-key': API_KEYS.key,
          'x-api-secret': API_KEYS.secret,
          'accept': 'application/json',
        },
      });
      if (!res.ok) throw new Error("Erreur lors du chargement des groupes d'application");
      setAppGroups(await res.json());
    } catch (e) { setErrorApp(e.toString()); }
    finally { setLoadingApp(false); }
  };

  useEffect(() => { fetchLocalGroups(); fetchAppGroups(); }, []);

  // --- AJOUT ---
  const [creating, setCreating] = useState(false);
  const [formError, setFormError] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: '', description: '' });
  const [addType, setAddType] = useState('local'); // 'local' ou 'app'

  const handleOpen = (type) => { setForm({ name: '', description: '' }); setFormError(null); setAddType(type); setOpen(true); };
  const handleClose = () => setOpen(false);
  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async e => {
    e.preventDefault();
    setCreating(true); setFormError(null);
    try {
      const url = addType === 'local' ? `${API_URL}/local-groups/` : `${API_URL}/app-groups/`;
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': API_KEYS.key,
          'x-api-secret': API_KEYS.secret,
        },
        body: JSON.stringify(form)
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        if (data.detail && data.detail.includes('exists')) {
          throw new Error("Ce nom de groupe existe déjà.");
        }
        throw new Error("Erreur lors de la création du groupe");
      }
      setOpen(false);
      if (addType === 'local') fetchLocalGroups();
      else fetchAppGroups();
    } catch (err) { setFormError(err.message); } finally { setCreating(false); }
  };

  // --- MODIFICATION ---
  const [editOpen, setEditOpen] = useState(false);
  const [editType, setEditType] = useState('local');
  const [editForm, setEditForm] = useState({ id: null, name: '', description: '' });
  const [editError, setEditError] = useState(null);
  const [editLoading, setEditLoading] = useState(false);

  const handleEditOpen = (g, type) => {
    setEditForm({ id: g.id, name: g.name, description: g.description });
    setEditType(type);
    setEditError(null);
    setEditOpen(true);
  };
  const handleEditClose = () => setEditOpen(false);
  const handleEditChange = e => setEditForm(f => ({ ...f, [e.target.name]: e.target.value }));
  const handleEditSubmit = async e => {
    e.preventDefault();
    setEditLoading(true); setEditError(null);
    try {
      const url = editType === 'local'
        ? `${API_URL}/local-groups/${editForm.id}`
        : `${API_URL}/app-groups/${editForm.id}`;
      const res = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': API_KEYS.key,
          'x-api-secret': API_KEYS.secret,
        },
        body: JSON.stringify({ name: editForm.name, description: editForm.description })
      });
      if (!res.ok) throw new Error('Erreur lors de la modification');
      setEditOpen(false);
      if (editType === 'local') fetchLocalGroups();
      else fetchAppGroups();
    } catch (err) { setEditError(err.message); } finally { setEditLoading(false); }
  };

  // --- SUPPRESSION ---
  const handleDelete = async (id, type) => {
    if (!window.confirm("Supprimer ce groupe ?")) return;
    try {
      const url = type === 'local' ? `${API_URL}/local-groups/${id}` : `${API_URL}/app-groups/${id}`;
      const res = await fetch(url, {
        method: 'DELETE',
        headers: {
          'x-api-key': API_KEYS.key,
          'x-api-secret': API_KEYS.secret,
        },
      });
      if (!res.ok) throw new Error(t('groups.add.local.deleteError'));
      if (type === 'local') fetchLocalGroups();
      else fetchAppGroups();
    } catch (err) { alert(err.message); }
  };

  return (
    <Box sx={{ p: 4, maxHeight: '95vh', overflow: 'auto' }}>
      <Typography variant="h5" mb={2}>{t('groups.title')}</Typography>
      <Paper sx={{ mb: 3 }}>
        <Tabs value={tab} onChange={(e, v) => setTab(v)} indicatorColor="primary" textColor="primary" centered>
          <Tab label={t('groups.local')} />
          <Tab label={t('groups.app')} />
        </Tabs>
      </Paper>
      {tab === 0 && (
        <Box>
          <Typography variant="h6" mb={2}>{t('groups.local.title')}</Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            {t('groups.local.desc')}
          </Alert>
          <Button variant="contained" onClick={() => handleOpen('local')} sx={{ mb: 2 }}>{t('groups.local.add')}</Button>
          {loadingLocal ? <CircularProgress /> : errorLocal ? <Typography color="error">{errorLocal}</Typography> : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('groups.local.group')}</TableCell>
                  <TableCell>{t('groups.local.description')}</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {localGroups.map(g => (
                  <TableRow key={g.id}>
                    <TableCell>{g.name}</TableCell>
                    <TableCell>{g.description}</TableCell>
                    <TableCell>
                      <IconButton onClick={() => handleEditOpen(g, 'local')} size="small" color="primary"><span role="img" aria-label="edit">✏️</span></IconButton>
                      <IconButton onClick={() => handleDelete(g.id, 'local')} size="small" color="error"><DeleteIcon /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Box>
      )}
      {tab === 1 && (
        <Box>
          <Typography variant="h6" mb={2}>{t('groups.app.title')}</Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            {t('groups.app.desc')}
          </Alert>
          <Button variant="contained" onClick={() => handleOpen('app')} sx={{ mb: 2 }}>
            {t('groups.app.add')}
          </Button>
          {loadingApp ? <CircularProgress /> : errorApp ? <Typography color="error">{errorApp}</Typography> : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('groups.app.group')}</TableCell>
                  <TableCell>{t('groups.app.description')}</TableCell>
                  <TableCell>{t('groups.app.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {appGroups.map(g => (
                  <TableRow key={g.id}>
                    <TableCell>{g.name}</TableCell>
                    <TableCell>{g.description}</TableCell>
                    <TableCell>
                      <IconButton onClick={() => handleEditOpen(g, 'app')} size="small" color="primary"><span role="img" aria-label="edit">✏️</span></IconButton>
                      <IconButton onClick={() => handleDelete(g.id, 'app')} size="small" color="error"><DeleteIcon /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Box>
      )}
      {/* Dialog d'ajout de groupe */}
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>{t('groups.add.local.title')}</DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
            <TextField margin="dense" label={t('groups.add.local.name')} name="name" value={form.name} onChange={handleChange} fullWidth required />
            <TextField margin="dense" label={t('groups.add.local.description')} name="description" value={form.description} onChange={handleChange} fullWidth />
            {formError && <Typography color="error" sx={{ mt: 1 }}>{formError}</Typography>}
            <DialogActions>
              <Button onClick={handleClose}>{t('groups.add.local.cancel')}</Button>
              <Button type="submit" variant="contained" disabled={creating}>{creating ? t('groups.add.local.creating') : t('groups.add.local.create')}</Button>
            </DialogActions>
          </Box>
        </DialogContent>
      </Dialog>
      {/* Dialog de modification de groupe */}
      <Dialog open={editOpen} onClose={handleEditClose}>
        <DialogTitle>Modifier le groupe</DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={handleEditSubmit} sx={{ mt: 1 }}>
            <TextField margin="dense" label={t('groups.add.local.name')} name="name" value={editForm.name} onChange={handleEditChange} fullWidth required />
            <TextField margin="dense" label={t('groups.add.local.description')} name="description" value={editForm.description} onChange={handleEditChange} fullWidth />
            {editError && <Typography color="error" sx={{ mt: 1 }}>{editError}</Typography>}
            <DialogActions>
              <Button onClick={handleEditClose}>{t('groups.add.local.cancel')}</Button>
              <Button type="submit" variant="contained" disabled={editLoading}>{editLoading ? t('groups.add.local.editing') : t('groups.add.local.edit')}</Button>
            </DialogActions>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
